/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.̦
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { Ref, VNode } from 'preact';
import { forwardRef } from 'preact/compat';
import { useImperativeHandle, useRef, useState } from 'preact/hooks';
import { useVisEvent } from '#hooks/PRIVATE_useVisEvents/useVisEvents';
import { getSectionalNavUtils } from './sectionalLegendNavUtils';
import { mergeProps } from '#utils/UNSAFE_mergeProps';
import { getRandomId } from '#utils/PRIVATE_visLayoutUtils/layoutUtils';
import {
  getAriaProps,
  getSectionGridNums,
  getLegendTextStyle,
  getIsLegendItemActionable,
  isEqualItem,
  getItemInfo,
  getKeyUpHandler
} from '#utils/PRIVATE_legendUtils/legendUtils';
import { useLegendDatatip } from '#UNSAFE_Legend/useLegendDatatip';
import { useItemFocus } from '#hooks/PRIVATE_useVisEvents';
import { getSectionalLegendLayoutInfo } from './getSectionalLegendLayout';
import { Flex } from '#UNSAFE_Flex';
import { styles } from '#PRIVATE_BaseLegend/themes/LegendStyles.css';
import { useCssVars } from '#hooks/PRIVATE_useCssVars';
import { legendVars } from '#utils/PRIVATE_legendUtils/legendUtils';
import { classNames } from '#utils/UNSAFE_classNames';
import { useTestId } from '#hooks/UNSAFE_useTestId';
import { BaseLegend } from '#PRIVATE_BaseLegend//BaseLegend';
import { useSectionalLegendContextMenu } from '#PRIVATE_BaseLegend/useLegendContextMenu';
import { SectionalLegendDragStartDetail, SectionalLegendProps } from './sectionalLegend.type';
import {
  ItemFocusDetail,
  ItemActionDetail,
  ItemInfo,
  LegendItemData,
  LegendSectionData
} from '#utils/UNSAFE_visTypes/legend';
import { useVisTouchResponse } from '#hooks/PRIVATE_useVisTouchResponse';
import { merge } from '@oracle/oraclejet-internal-utilities/stringUtils';
import { useTextDimensions } from '#hooks/PRIVATE_useTextDimensions';
import { TrackResizeContainer } from '#UNSAFE_TrackResizeContainer';
import { useLegendDnd } from '#hooks/PRIVATE_useLegendDnD';
import { styles as VisDndStyles } from '#hooks/PRIVATE_useVisDnd/themes/VisDndStyles.css';
import { LegendText } from '#PRIVATE_BaseLegend/LegendText';

export type SizeHandle = {
  _getPreferredSize: (
    _width: number,
    _height: number
  ) => { width: number; height: number } | undefined;
};

type ForwardRefSectionalLegend = <
  K extends string | number,
  D extends LegendItemData<K>,
  S extends LegendSectionData<K, D>
>(
  props: SectionalLegendProps<K, D, S> & { ref?: Ref<SizeHandle> }
) => VNode | null;

/**
 * A Sectional Legend allows grouping of legend items in sections with a specific title.
 */
export const SectionalLegend = forwardRef(
  <K extends string | number, D extends LegendItemData<K>, S extends LegendSectionData<K, D>>(
    {
      orientation = 'horizontal',
      sectionTitleHAlign = 'start',
      sections,
      isReadOnly = true,
      onItemAction,
      onItemHover,
      onItemFocus,
      testId,
      contextMenuConfig,
      hideAndShowBehavior = 'off',
      hoverBehavior = 'none',
      valign = 'top',
      halign = 'start',
      ...props
    }: SectionalLegendProps<K, D, S>,
    ref: Ref<SizeHandle> = null
  ) => {
    return (
      <TrackResizeContainer
        width={'100%'}
        height={'100%'}
        class={classNames([
          styles.flexStyle,
          styles[`${halign}HAlign`],
          styles[`${valign}VAlign`]
        ])}>
        {function _(width, _height) {
          const testIdProps = useTestId(testId);
          const rootRef = useRef<HTMLDivElement>(null);
          const isTestEnv = testIdProps['data-testid'] !== undefined;
          const rootDimsRef = useRef<DOMRect>();
          const { resolvedVars, cssContent } = useCssVars(legendVars);

          const { getDetailFromInfo, getPrevItemInfo, getNextItemInfo } = getSectionalNavUtils<
            K,
            D,
            S
          >(sections);
          const legendFontStylesObj = getLegendTextStyle(
            {
              fontStyle: props.textFontStyle,
              fontSize: props.textFontSize?.toString(),
              fontWeight: props.textFontWeight?.toString(),
              fontFamily: props.textFontFamily
            },
            resolvedVars
          );
          const { textMeasureContent, getTextDimensions } = useTextDimensions();

          const isLegendItemActionable = getIsLegendItemActionable(sections, isReadOnly, true);
          const hasData = sections.length > 0;

          const layoutInfo = getSectionalLegendLayoutInfo({
            width,
            rootRef,
            getTextDimensions,
            resolvedVars,
            orientation,
            legendFontStylesObj,
            symbolWidth: props.symbolWidth,
            symbolHeight: props.symbolHeight,
            sections
          });
          const { touchResponse, touchResponseStyle } = useVisTouchResponse({ type: 'touchStart' });

          const {
            focusedItemInfo,
            hoveredItemInfo,
            onContextMenuDismissed,
            activeId,
            eventsProps
          } = useVisEvent<ItemInfo, ItemActionDetail<K, D>, ItemFocusDetail<K, D>, D>(
            touchResponse,
            !isReadOnly,
            isLegendItemActionable,
            getKeyUpHandler,
            getDetailFromInfo,
            getPrevItemInfo,
            getNextItemInfo,
            onItemAction,
            onItemHover,
            onItemFocus,
            isEqualItem,
            getItemInfo,
            { itemIdx: 0, sectionIdx: 0 }
          );

          const { focusedItemRef } = useItemFocus(focusedItemInfo);
          const [datatipDisabled, setDatatipDisabled] = useState(false);
          const {
            datatipContent,
            datatipProps: { 'aria-describedby': datatipAriaDescribedby, ...datatipProps }
          } = useLegendDatatip({
            sections,
            datatip: props.datatip,
            rootDimsRef,
            focusedItemInfo,
            focusedItemRef,
            hoveredItemInfo,
            touchResponse,
            isDisabled: datatipDisabled
          });
          const ariaProps = getAriaProps<K>(
            isReadOnly,
            props['aria-label'],
            props.hiddenIds,
            merge([props['aria-describedby'], datatipAriaDescribedby]),
            props['aria-labelledby']
          );

          const { contextMenuContent, contextMenuProps } = useSectionalLegendContextMenu<K, D, S>({
            rootRef,
            contextMenuConfig: hasData ? contextMenuConfig : undefined,
            focusedItemInfo,
            sections,
            isReadOnly,
            onContextMenuDismissed
          });

          const [dragOver, setDragOver] = useState<boolean>();
          const { draggable, dndProps, dndContent } = useLegendDnd<
            K,
            D,
            SectionalLegendDragStartDetail<D>,
            S
          >({
            onDrag: props.onDrag,
            onDragStart: props.onDragStart,
            onDragEnd: props.onDragEnd,
            onDragEnter: props.onDragEnter,
            onDragLeave: props.onDragLeave,
            onDragOver: props.onDragOver,
            onDrop: props.onDrop,
            sections,
            setDragOver,
            setDatatipDisabled,
            itemDraggable: props.itemDraggable
          });

          const mergedProps = mergeProps(eventsProps, datatipProps, contextMenuProps, dndProps);
          const titleStyle = {
            fontFamily: props.sectionTitleFontFamily,
            fontSize: props.sectionTitleFontSize,
            color: props.sectionTitleColor,
            fontStyle: props.sectionTitleFontStyle,
            fontWeight: props.sectionTitleFontWeight,
            textDecoration: props.sectionTitleTextDecoration
          };
          const { sectionalLegendBaseStyles, renderedLegendStyle } = styles;
          const isHoriz = orientation === 'horizontal';

          useImperativeHandle(
            ref,
            () => ({
              _getPreferredSize: (_width: number, _height: number) => {
                const legendLayoutInfo = getSectionalLegendLayoutInfo({
                  width: _width,
                  getTextDimensions,
                  resolvedVars,
                  rootRef,
                  orientation,
                  legendFontStylesObj,
                  symbolWidth: props.symbolWidth,
                  symbolHeight: props.symbolHeight,
                  sections
                });
                if (legendLayoutInfo) {
                  return {
                    width: legendLayoutInfo?.maxRowWidth,
                    height: legendLayoutInfo?.legendHeight
                  };
                } else {
                  return;
                }
              }
            }),
            [getTextDimensions, legendFontStylesObj, resolvedVars]
          );

          return (
            <div
              ref={rootRef}
              tabIndex={isReadOnly ? undefined : 0}
              role="application"
              class={classNames([
                sectionalLegendBaseStyles,
                renderedLegendStyle,
                dragOver
                  ? VisDndStyles.dndDragOverHTMLStyle
                  : dragOver === false
                  ? VisDndStyles.dndRejectedStyle
                  : undefined
              ])}
              draggable={draggable}
              style={touchResponseStyle}
              {...testIdProps}
              {...ariaProps}
              {...mergedProps}
              aria-activedescendant={activeId}>
              <Flex direction={isHoriz ? 'row' : 'column'} wrap={'wrap'} gap={'4x'}>
                {sections.map((section: S, index: number) => {
                  const labelledById = !isReadOnly ? getRandomId() : '';
                  const isWrappedSection = layoutInfo
                    ? layoutInfo.isSectionWrappedArray[index]
                    : false;
                  const flexDirection = !isHoriz || isWrappedSection ? 'column' : 'row';
                  const alignItems = !isHoriz || isWrappedSection ? 'start' : 'center';
                  const { itemsPerCol, itemsPerRow } = getSectionGridNums(
                    section.items.length,
                    layoutInfo?.itemsPerRowArray[index]
                  );
                  return (
                    <Flex
                      align={alignItems}
                      direction={flexDirection}
                      maxWidth={'100%'}
                      justify={'start'}
                      wrap="wrap">
                      <LegendText
                        text={section.title}
                        {...titleStyle}
                        type="title"
                        id={labelledById}
                        align={sectionTitleHAlign}
                      />
                      <BaseLegend
                        {...props}
                        focusedItemRef={focusedItemRef}
                        labelledBy={labelledById}
                        sectionIdx={index}
                        items={section.items}
                        orientation={orientation}
                        itemsPerRow={itemsPerRow}
                        itemsPerCol={itemsPerCol}
                        itemWidth={
                          layoutInfo?.isLegendWrappedArray[index]
                            ? layoutInfo?.maxItemWidth
                            : undefined
                        }
                        isReadOnly={isReadOnly}
                        focusedItemInfo={focusedItemInfo}
                        hoveredItemInfo={hoveredItemInfo}
                        activeId={activeId}
                        hideAndShowBehavior={hideAndShowBehavior}
                        hoverBehavior={hoverBehavior}
                        isTestEnv={isTestEnv}
                      />
                    </Flex>
                  );
                })}
              </Flex>
              {datatipContent}
              {textMeasureContent}
              {cssContent}
              {contextMenuContent}
              {dndContent}
            </div>
          );
        }}
      </TrackResizeContainer>
    );
  }
) as ForwardRefSectionalLegend;
